<template>
    <CCard v-if="!chatMeta.chat_id && orderId && customerId">
        <CCardHeader>
            <strong>Чат по заказу</strong>
        </CCardHeader>
        <CCardBody>
            <div v-if="loading" class="text-center py-3">
                <CSpinner color="primary" />
            </div>
            <template v-else>
                <p class="text-muted mb-3">Чат по этому заказу ещё не создан.</p>
                <CButton
                    color="primary"
                    :disabled="creatingChat"
                    @click="createOrderChat"
                >
                    {{ creatingChat ? 'Создаём...' : 'Начать чат с клиентом' }}
                </CButton>
            </template>
        </CCardBody>
    </CCard>
    <CCard v-else-if="chatMeta.chat_id">
        <CCardHeader class="d-flex justify-content-between align-items-center">
            <strong>Чат по заказу</strong>
            <div class="d-flex align-items-center">
                <CBadge :color="chatMeta.status === 'closed' ? 'secondary' : 'warning'">
                    {{ formatChatStatus(chatMeta.status) }}
                </CBadge>
            </div>
        </CCardHeader>
        <CCardBody>
            <div v-if="loading" class="text-center py-3">
                <CSpinner color="primary" />
            </div>
            <template v-else>
                <div class="order-chat__actions mb-3">
                    <CLink :to="{ name: 'Chat', params: { chatId: chatMeta.chat_id } }">
                        Открыть в разделе чатов
                    </CLink>
                </div>
                <div ref="chatMessages" class="order-chat__messages">
                    <div
                        v-for="message in chatMessages"
                        :key="message.id"
                        :class="[
                            'order-chat__message',
                            message.sender_type === 'admin'
                                ? 'order-chat__message--admin'
                                : message.sender_type === 'system'
                                    ? 'order-chat__message--system'
                                    : 'order-chat__message--customer'
                        ]"
                    >
                        <div v-if="message.attachments && message.attachments.length" class="order-chat__attachments">
                            <div
                                v-for="attachment in message.attachments"
                                :key="attachment.id"
                                class="order-chat__attachment"
                            >
                                <img
                                    v-if="isImageAttachment(attachment)"
                                    :src="attachment.url"
                                    :alt="attachment.original_name"
                                    class="order-chat__attachment-image"
                                />
                                <video
                                    v-else-if="isVideoAttachment(attachment)"
                                    :src="attachment.url"
                                    controls
                                    preload="metadata"
                                    class="order-chat__attachment-video"
                                ></video>
                            </div>
                        </div>
                        <div class="order-chat__message-text">
                            <ChatMessageText :text="message.message" />
                        </div>
                        <div class="order-chat__message-time">
                            <span>{{ formatChatDate(message.created_at) }}</span>
                            <span v-if="message.edited_at" class="order-chat__message-edited">изменено</span>
                            <ChatMessageEdit
                                :message="message"
                                :can-edit="canEditMessage(message)"
                                :saving="editingMessageId === message.id"
                                @save="saveEditedMessage"
                            />
                        </div>
                    </div>
                    <div v-if="!chatMessages.length" class="order-chat__empty">
                        Нет сообщений
                    </div>
                </div>
                <div v-if="chat.pendingFiles.length" class="order-chat__pending">
                    <div
                        v-for="(item, index) in chat.pendingFiles"
                        :key="item.id"
                        class="order-chat__pending-item"
                    >
                        <div class="order-chat__pending-name">{{ item.file.name }}</div>
                        <button
                            type="button"
                            class="order-chat__pending-remove"
                            @click="removePendingFile(index)"
                        >
                            &times;
                        </button>
                    </div>
                </div>
                <div class="order-chat__composer">
                    <CTextarea
                        v-model.trim="chat.draft"
                        rows="3"
                        placeholder="Введите сообщение клиенту"
                        style="margin-bottom: 0;"
                        :disabled="!chat.connected || chat.sending || isChatClosed"
                        @keydown.enter.exact.prevent="sendChatMessage"
                    />
                    <div class="order-chat__composer-actions">
                        <CButton
                            color="success"
                            class="mr-2"
                            :disabled="resolvingChat || isChatClosed"
                            @click="resolveChat"
                        >
                            {{ resolvingChat ? 'Закрываем...' : 'Вопрос решен' }}
                        </CButton>
                        <ChatQuickReplyPicker
                            :disabled="isAttachDisabled"
                            :customer="order.customer || {}"
                            :chat="chatMeta"
                            :order="order"
                            @select="onQuickReplySelect"
                        />
                        <label class="order-chat__attach" :class="{ 'order-chat__attach--disabled': isAttachDisabled }">
                            <input
                                type="file"
                                hidden
                                multiple
                                accept="image/*,video/*"
                                :disabled="isAttachDisabled"
                                @change="onFilesSelected"
                            >
                            📎
                        </label>
                        <CButton
                            color="primary"
                            class="order-chat__send"
                            aria-label="Отправить сообщение"
                            :disabled="!canSendMessage"
                            @click="sendChatMessage"
                        >
                            <span class="order-chat__send-icon">➤</span>
                        </CButton>
                    </div>
                </div>
            </template>
        </CCardBody>
    </CCard>
</template>

<script>
    import Vue from 'vue';
    import moment from 'moment';
    import { io } from 'socket.io-client';
    import ChatQuickReplyPicker from '@/components/chats/ChatQuickReplyPicker';
    import ChatMessageEdit from '@/components/chats/ChatMessageEdit';
    import ChatMessageText from '@/components/chats/ChatMessageText';

    export default {
        name: 'OrderChat',

        components: {
            ChatQuickReplyPicker,
            ChatMessageEdit,
            ChatMessageText
        },

        props: {
            order: {
                type: Object,
                default: () => ({})
            }
        },

        data() {
            return {
                loading: true,
                chatMeta: {},
                chat: {
                    socket: null,
                    connected: false,
                    messages: [],
                    draft: '',
                    sending: false,
                    pendingFiles: []
                },
                resolvingChat: false,
                creatingChat: false,
                editingMessageId: null,
                currentAdminId: null
            };
        },

        computed: {
            chatMessages() {
                return this.chat.messages;
            },
            isChatClosed() {
                return this.chatMeta?.status === 'closed';
            },
            canSendMessage() {
                return this.chat.connected
                    && !this.chat.sending
                    && !this.isChatClosed
                    && !!this.chatMeta.chat_id
                    && (!!this.chat.draft || this.chat.pendingFiles.length > 0);
            },
            isAttachDisabled() {
                return !this.chat.connected || this.chat.sending || this.isChatClosed || !this.chatMeta.chat_id;
            },
            customerId() {
                return Number(this.order?.customer?.id) || 0;
            },
            orderId() {
                return Number(this.order?.id) || 0;
            }
        },

        watch: {
            orderId: {
                immediate: true,
                handler(orderId) {
                    if (!orderId) {
                        this.chatMeta = {};
                        this.chat.messages = [];
                        this.loading = false;
                        return;
                    }
                    this.loadOrderChat();
                }
            }
        },

        mounted() {
            try {
                this.currentAdminId = Number(JSON.parse(localStorage.getItem('profile') || '{}').id) || null;
            } catch (e) {
                this.currentAdminId = null;
            }
            this.initChat();
        },

        beforeDestroy() {
            this.clearPendingFiles();
            if (this.chat.socket) {
                this.chat.socket.disconnect();
                this.chat.socket = null;
            }
        },

        methods: {
            getSocketBaseUrl() {
                const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || '';
                return apiBaseUrl
                    .replace(/\/api\/admin\/?$/, '')
                    .replace(/\/api\/?$/, '')
                    .replace(/\/admin\/?$/, '');
            },
            async loadOrderChat() {
                if (!this.orderId) {
                    return;
                }

                this.loading = true;
                try {
                    const { data } = await Vue.axios.get(`/chats/order/${this.orderId}`);
                    const chat = data?.chat || null;
                    if (!chat?.chat_id) {
                        this.chatMeta = {};
                        this.chat.messages = [];
                        return;
                    }

                    this.applyChatMeta(chat);
                } catch {
                    this.chatMeta = {};
                    this.chat.messages = [];
                } finally {
                    this.loading = false;
                }
            },
            applyChatMeta(chat) {
                this.chatMeta = chat;
                if (this.chat.connected && this.chat.socket && this.customerId) {
                    this.chat.socket.emit('chat:subscribe', {
                        customer_id: this.customerId,
                        chat_id: Number(chat.chat_id)
                    });
                    this.fetchChatHistory();
                } else if (this.chat.connected) {
                    this.fetchChatHistory();
                }
            },
            async createOrderChat() {
                if (!this.orderId || this.creatingChat) {
                    return;
                }

                this.creatingChat = true;
                try {
                    const { data } = await Vue.axios.post(`/chats/order/${this.orderId}`);
                    if (data?.chat?.chat_id) {
                        this.applyChatMeta(data.chat);
                    }
                } catch {
                    this.$root.$emit('showError', 'Не удалось создать чат по заказу');
                } finally {
                    this.creatingChat = false;
                }
            },
            initChat() {
                const token = localStorage.getItem('user-token');
                if (!token) {
                    return;
                }

                this.chat.socket = io(this.getSocketBaseUrl(), {
                    path: '/socket.io',
                    reconnection: true,
                    transports: ['websocket'],
                    auth: {
                        role: 'admin',
                        token
                    },
                    query: {
                        role: 'admin',
                        token
                    }
                });

                this.chat.socket.on('connect', () => {
                    this.chat.connected = true;
                    if (this.customerId) {
                        this.chat.socket.emit('chat:subscribe', {
                            customer_id: this.customerId,
                            chat_id: Number(this.chatMeta.chat_id) || undefined
                        });
                    }
                    if (this.chatMeta.chat_id) {
                        this.fetchChatHistory();
                    }
                });

                this.chat.socket.on('disconnect', () => {
                    this.chat.connected = false;
                });

                this.chat.socket.on('connect_error', err => {
                    this.chat.connected = false;
                    this.$root.$emit('showError', `Не удалось подключиться к сокету чата: ${err && err.message ? err.message : 'unknown error'}`);
                });

                this.chat.socket.on('chat:message:new', message => {
                    if (+message.customer_id !== +this.customerId || +message.chat_id !== +this.chatMeta.chat_id) {
                        return;
                    }
                    this.upsertChatMessage(message);
                    this.updateChatMetaFromMessage(message);
                    this.markRead(message);
                    this.scrollChatToBottom();
                });

                this.chat.socket.on('chat:message:read', message => {
                    if (+message.customer_id !== +this.customerId || +message.chat_id !== +this.chatMeta.chat_id) {
                        return;
                    }
                    this.upsertChatMessage(message);
                });

                this.chat.socket.on('chat:message:edited', message => {
                    if (+message.customer_id !== +this.customerId || +message.chat_id !== +this.chatMeta.chat_id) {
                        return;
                    }
                    this.upsertChatMessage(message);
                });

                this.chat.socket.on('chat:status:updated', summary => {
                    if (+summary.chat_id !== +this.chatMeta.chat_id) {
                        return;
                    }
                    this.chatMeta = {
                        ...this.chatMeta,
                        ...summary
                    };
                });

                this.chat.socket.on('chat:error', payload => {
                    const text = payload && payload.error ? payload.error : 'Ошибка чата';
                    this.$root.$emit('showError', text);
                });
            },
            fetchChatHistory() {
                if (!this.chat.socket || !this.chatMeta.chat_id) {
                    return;
                }

                this.chat.socket.emit('chat:history', {
                    chat_id: Number(this.chatMeta.chat_id),
                    limit: 100,
                    offset: 0
                }, response => {
                    if (!response || !response.ok) {
                        return;
                    }

                    const history = response.messages || [];
                    this.chat.messages = [];
                    history.forEach(this.upsertChatMessage);
                    this.chatMessages.forEach(this.markRead);
                    this.scrollChatToBottom();
                });
            },
            onQuickReplySelect(text) {
                this.chat.draft = String(text || '');
            },
            canEditMessage(message) {
                return message
                    && message.sender_type === 'admin'
                    && this.currentAdminId
                    && Number(message.sender_id) === Number(this.currentAdminId);
            },
            saveEditedMessage({ messageId, message, done }) {
                if (!this.chat.socket || !messageId || this.editingMessageId) {
                    if (typeof done === 'function') {
                        done(false);
                    }
                    return;
                }
                this.editingMessageId = Number(messageId);
                this.chat.socket.emit('chat:message:edit', {
                    message_id: Number(messageId),
                    message
                }, response => {
                    this.editingMessageId = null;
                    if (!response || !response.ok) {
                        this.$root.$emit('showError', (response && response.error) || 'Не удалось изменить сообщение');
                        if (typeof done === 'function') {
                            done(false);
                        }
                        return;
                    }
                    this.upsertChatMessage(response.message);
                    if (typeof done === 'function') {
                        done(true);
                    }
                });
            },
            async sendChatMessage() {
                if (!this.canSendMessage || !this.chat.socket) {
                    return;
                }

                this.chat.sending = true;
                try {
                    const attachmentIds = await this.uploadPendingFiles();
                    this.chat.socket.emit('chat:message', {
                        chat_id: Number(this.chatMeta.chat_id),
                        message: this.chat.draft,
                        attachment_ids: attachmentIds
                    }, response => {
                        this.chat.sending = false;
                        if (!response || !response.ok) {
                            this.$root.$emit('showError', response && response.error ? response.error : 'Не удалось отправить сообщение');
                            return;
                        }
                        this.chat.draft = '';
                        this.clearPendingFiles();
                        this.scrollChatToBottom();
                    });
                } catch (e) {
                    this.chat.sending = false;
                    this.$root.$emit('showError', 'Не удалось загрузить вложения');
                }
            },
            async uploadPendingFiles() {
                const ids = [];
                for (const item of this.chat.pendingFiles) {
                    const formData = new FormData();
                    formData.append('chat_id', this.chatMeta.chat_id);
                    formData.append('file', item.file);
                    const { data } = await Vue.axios.post('/chats/attachments', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                    if (data?.attachment?.id) {
                        ids.push(data.attachment.id);
                    }
                }
                return ids;
            },
            onFilesSelected(event) {
                const files = Array.from(event.target.files || []);
                files.forEach(file => {
                    if (!this.isAllowedMimeType(file.type)) {
                        this.$root.$emit('showError', 'Можно отправлять только фото и видео');
                        return;
                    }
                    if (file.size > 20 * 1000 * 1000) {
                        this.$root.$emit('showError', 'Размер файла не должен превышать 20 МБ');
                        return;
                    }
                    this.chat.pendingFiles.push({
                        id: `${Date.now()}-${file.name}-${this.chat.pendingFiles.length}`,
                        file
                    });
                });
                event.target.value = '';
            },
            removePendingFile(index) {
                this.chat.pendingFiles.splice(index, 1);
            },
            clearPendingFiles() {
                this.chat.pendingFiles = [];
            },
            markRead(message) {
                if (!message || !message.id || message.sender_type !== 'customer' || message.read_at || !this.chat.socket) {
                    return;
                }
                this.chat.socket.emit('chat:read', { message_id: message.id });
            },
            upsertChatMessage(message) {
                if (!message || !message.id || +message.chat_id !== +this.chatMeta.chat_id) {
                    return;
                }

                const messages = this.chat.messages.slice();
                const idx = messages.findIndex(item => +item.id === +message.id);
                if (idx >= 0) {
                    messages.splice(idx, 1, {
                        ...messages[idx],
                        ...message
                    });
                } else {
                    messages.push(message);
                }
                messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                this.chat.messages = messages;
            },
            updateChatMetaFromMessage(message) {
                if (!message || message.sender_type === 'system') {
                    return;
                }

                const attachmentsCount = Array.isArray(message.attachments) ? message.attachments.length : 0;
                const lastMessage = String(message.message || '').trim() || (attachmentsCount > 1 ? `${attachmentsCount} вложения` : attachmentsCount === 1 ? 'Вложение' : '');
                this.chatMeta = {
                    ...this.chatMeta,
                    last_message: lastMessage,
                    last_message_at: message.created_at,
                    last_sender_type: message.sender_type,
                    unread_count: 0
                };
            },
            async resolveChat() {
                if (!this.chatMeta.chat_id || this.resolvingChat || this.isChatClosed) {
                    return;
                }

                this.resolvingChat = true;
                try {
                    const { data } = await Vue.axios.post(`/chats/${this.chatMeta.chat_id}/close`);
                    this.chatMeta = {
                        ...this.chatMeta,
                        ...data
                    };
                } catch {
                    this.$root.$emit('showError', 'Не удалось закрыть обращение');
                } finally {
                    this.resolvingChat = false;
                }
            },
            formatChatStatus(status) {
                return status === 'closed' ? 'Закрыт' : 'В работе';
            },
            formatChatDate(value) {
                if (!value) {
                    return '';
                }
                return moment(value).format('DD.MM.YYYY HH:mm');
            },
            isImageAttachment(attachment) {
                return String(attachment?.mime_type || '').startsWith('image/');
            },
            isVideoAttachment(attachment) {
                return String(attachment?.mime_type || '').startsWith('video/');
            },
            isAllowedMimeType(mimeType) {
                const value = String(mimeType || '');
                return value.startsWith('image/') || value.startsWith('video/');
            },
            scrollChatToBottom() {
                this.$nextTick(() => {
                    if (this.$refs.chatMessages) {
                        this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
                    }
                });
            }
        }
    };
</script>

<style scoped>
.order-chat__actions {
    font-size: 14px;
}

.order-chat__messages {
    max-height: 420px;
    overflow-y: auto;
    border: 1px solid #e4e7ea;
    border-radius: 8px;
    padding: 10px;
    background: #ffffff;
}

.order-chat__message {
    width: fit-content;
    max-width: 80%;
    padding: 8px 10px;
    margin-bottom: 8px;
    border-radius: 8px;
}

.order-chat__message--admin {
    margin-left: auto;
    background: #321fdb;
    color: #ffffff;
}

.order-chat__message--customer {
    margin-right: auto;
    background: #f1f3f5;
}

.order-chat__message--system {
    margin-left: auto;
    margin-right: auto;
    background: #fff8e7;
    color: #6b4e16;
}

.order-chat__message-text {
    white-space: pre-wrap;
    word-break: break-word;
}

.order-chat__message-time {
    margin-top: 4px;
    font-size: 11px;
    opacity: 0.75;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
}

.order-chat__message-edited {
    font-style: italic;
}

.order-chat__attachments {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 8px;
}

.order-chat__attachment-image,
.order-chat__attachment-video {
    display: block;
    max-width: 240px;
    border-radius: 8px;
}

.order-chat__composer {
    margin-top: 12px;
}

.order-chat__composer-actions {
    display: flex;
    align-items: center;
}

.order-chat__attach {
    width: 36px;
    height: 36px;
    border: 1px solid #ced4da;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin-bottom: 0;
    margin-right: 8px;
}

.order-chat__attach--disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.order-chat__send {
    width: 36px;
    height: 36px;
    min-width: 36px;
    border-radius: 6px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.order-chat__send-icon {
    font-size: 14px;
    line-height: 1;
}

.order-chat__pending {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 10px 0 -10px 0;
}

.order-chat__pending-item {
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 100%;
    padding: 6px 10px;
    border-radius: 8px;
    background: #f3f4f6;
}

.order-chat__pending-name {
    font-size: 12px;
}

.order-chat__pending-remove {
    width: 20px;
    border: 0;
    background: transparent;
    font-size: 18px;
    line-height: 1;
}

.order-chat__empty {
    color: #8a93a2;
    text-align: center;
    padding: 24px 12px;
}
</style>
